// /admin/ads → "🚀 השקת בדיקה": pick 4 of the new reels and 4 of the old videos, choose the
// flows and budget, and create (and optionally start) the test in one go.
//   new vs old (default): per flow, two ads with the same budget — one carrying the 4 new
//     reels, one the 4 old videos (Meta dynamic creative inside each) → is new better than old,
//     and which video wins inside each group
//   each video alone: every video × flow is its own ad set (exact, but needs more budget)
import {FC, useEffect, useState} from 'react'
import {Creative, createTest, getCreatives, getVideoSource} from './_requests'

const MAX = 4
const ils = (n: number | null | undefined) =>
  n == null || !isFinite(n) ? '—' : `₪${Math.round(n).toLocaleString('he-IL')}`
const errMsg = (e: any) => e?.response?.data?.error || e?.message || 'שגיאה'
const secs = (s?: number) =>
  s
    ? s >= 60
      ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`
      : `${Math.round(s)} שנ׳`
    : ''

const Pick: FC<{
  c: Creative
  picked: boolean
  disabled: boolean
  onPick: () => void
  badge?: string
}> = ({c, picked, disabled, onPick, badge}) => {
  const [src, setSrc] = useState<string | null>(null)
  const play = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (c.mp4) return setSrc(c.mp4)
    try {
      const {data} = await getVideoSource(c.video_id!)
      setSrc(data.source)
    } catch (_) {}
  }
  return (
    <div
      className='card card-bordered p-2'
      onClick={() => !disabled && onPick()}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        outline: picked ? '3px solid #2b70fa' : undefined,
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <div style={{position: 'relative'}}>
        {src ? (
          <video
            src={src}
            controls
            autoPlay
            playsInline
            onClick={(e) => e.stopPropagation()}
            style={{width: '100%', aspectRatio: '9 / 16', borderRadius: 8, background: '#000'}}
          />
        ) : (
          <div
            style={{
              width: '100%',
              aspectRatio: '9 / 16',
              borderRadius: 8,
              background: `#111 url("${c.thumb}") center/cover no-repeat`,
            }}
          >
            <button
              className='btn btn-sm btn-dark'
              onClick={play}
              style={{
                position: 'absolute',
                bottom: 8,
                insetInlineStart: 8,
                borderRadius: 999,
                opacity: 0.85,
              }}
            >
              ▶ צפייה
            </button>
          </div>
        )}
        <span
          className={`badge ${picked ? 'badge-primary' : 'badge-light'}`}
          style={{position: 'absolute', top: 8, insetInlineStart: 8, fontSize: 13}}
        >
          {picked ? '✓ נבחר' : 'בחירה'}
        </span>
        {badge && (
          <span
            className='badge badge-success'
            style={{position: 'absolute', top: 8, insetInlineEnd: 8}}
          >
            {badge}
          </span>
        )}
      </div>
      <div className='fw-bold fs-8 mt-2 text-truncate' title={c.title}>
        {c.title || 'סרטון'}
      </div>
      <div className='text-muted' style={{fontSize: 11}}>
        {secs(c.length)}
        {c.kind === 'video' &&
          (c.stats.leads
            ? ` · ${c.stats.leads} לידים · ${ils(c.stats.cpl)} לליד`
            : ' · בלי תוצאות')}
        {c.kind === 'reel' && c.concept ? ` · ${c.concept}` : ''}
      </div>
    </div>
  )
}

const LaunchTest: FC<{lookalikes: string[]; onCreated: () => void; goTests: () => void}> = ({
  lookalikes,
  onCreated,
  goTests,
}) => {
  const [lib, setLib] = useState<Creative[] | null>(null)
  const [newSel, setNewSel] = useState<string[]>([])
  const [oldSel, setOldSel] = useState<string[]>([])
  const [layout, setLayout] = useState<'groups' | 'cells'>('groups')
  const [flows, setFlows] = useState<string[]>(['website', 'lead_form'])
  const [audience, setAudience] = useState('broad')
  const [budget, setBudget] = useState(50)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ok: boolean; text: string} | null>(null)
  const [oldSort, setOldSort] = useState<'cpl' | 'spend'>('cpl')

  const loadLib = () => {
    setMsg(null)
    getCreatives(false)
      .then(({data}) => setLib(data.creatives))
      .catch((e) => setMsg({ok: false, text: errMsg(e)}))
  }

  useEffect(() => {
    loadLib()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const reels = (lib || []).filter((c) => c.kind === 'reel')
  const olds = (lib || [])
    .filter((c) => c.kind === 'video')
    .sort((a, b) =>
      oldSort === 'spend'
        ? b.stats.spend - a.stats.spend
        : (a.stats.leads >= 5 ? a.stats.cpl! : Infinity) -
            (b.stats.leads >= 5 ? b.stats.cpl! : Infinity) || b.stats.spend - a.stats.spend
    )
  const proven = olds.filter((c) => c.stats.leads >= 10).sort((a, b) => a.stats.cpl! - b.stats.cpl!)
  const topOld = proven.slice(0, 3).map((c) => c.key)

  const toggle = (list: string[], set: (v: string[]) => void, k: string) =>
    set(list.includes(k) ? list.filter((x) => x !== k) : list.length < MAX ? [...list, k] : list)

  const all = [...newSel, ...oldSel]
  const ads =
    layout === 'groups'
      ? (newSel.length ? 1 : 0) * flows.length + (oldSel.length ? 1 : 0) * flows.length
      : all.length * flows.length
  const perDay = ads * budget
  const groupProblem =
    layout === 'groups' &&
    ((newSel.length > 0 && newSel.length < 2) || (oldSel.length > 0 && oldSel.length < 2))
      ? 'בכל קבוצה צריך לפחות 2 סרטונים (או 0)'
      : ''
  const cellsProblem =
    layout === 'cells' && ads > 12 ? `${ads} מודעות — עד 12. הורידו סרטונים או דרך אחת` : ''
  const problem = !all.length
    ? 'בחרו סרטונים'
    : !flows.length
    ? 'בחרו לפחות דרך אחת'
    : groupProblem || cellsProblem

  const submit = async (start: boolean) => {
    if (
      start &&
      !window.confirm(
        `ליצור ${ads} מודעות ולהפעיל אותן ברגע שייבנו?\nתקציב: ₪${perDay.toLocaleString(
          'he-IL'
        )} ליום בסך הכל.`
      )
    )
      return
    setBusy(true)
    setMsg(null)
    try {
      const stamp = new Date().toLocaleDateString('he-IL', {day: '2-digit', month: '2-digit'})
      if (layout === 'groups') {
        if (newSel.length)
          await createTest({
            creative_keys: newSel,
            destinations: flows,
            mode: 'one_ad',
            audience,
            daily_budget: budget,
            name: `רילס חדשים ${stamp}`,
            start,
          })
        if (oldSel.length)
          await createTest({
            creative_keys: oldSel,
            destinations: flows,
            mode: 'one_ad',
            audience,
            daily_budget: budget,
            name: `סרטונים ישנים ${stamp}`,
            start,
          })
      } else {
        await createTest({
          creative_keys: all,
          destinations: flows,
          mode: 'cells',
          audience,
          daily_budget: budget,
          name: `חדשים מול ישנים ${stamp}`,
          start,
        })
      }
      setMsg({
        ok: true,
        text: start
          ? `נוצר! ${ads} מודעות נבנות עכשיו (העלאת סרטונים לוקחת כמה דקות) ויופעלו אוטומטית ברגע שכולן מוכנות. אפשר לעקוב בלשונית "בדיקות A/B".`
          : `נוצר! ${ads} מודעות נבנות עכשיו במצב מושהה. מפעילים מלשונית "בדיקות A/B".`,
      })
      setNewSel([])
      setOldSel([])
      onCreated()
    } catch (e) {
      setMsg({ok: false, text: errMsg(e)})
    } finally {
      setBusy(false)
    }
  }

  if (!lib)
    return msg ? (
      <div className='alert alert-danger d-flex align-items-center gap-3 fs-7'>
        לא הצלחנו לטעון את הסרטונים: {msg.text}
        <button className='btn btn-sm btn-light' onClick={loadLib}>
          נסו שוב
        </button>
      </div>
    ) : (
      <div className='text-muted'>טוען את הסרטונים…</div>
    )

  const grid = {gridTemplateColumns: 'repeat(auto-fill, minmax(min(150px, 100%), 1fr))'}

  return (
    <div style={{paddingBottom: 140}}>
      <div className='d-flex justify-content-between align-items-end flex-wrap gap-2 mb-3'>
        <div>
          <h4 className='fw-bold mb-1'>1. רילס חדשים — בחרו {MAX}</h4>
          <div className='text-muted fs-8'>
            נבחרו {newSel.length} מתוך {MAX}. לוחצים על כרטיס כדי לבחור, "צפייה" כדי לראות.
          </div>
        </div>
      </div>
      <div className='d-grid gap-3 mb-6' style={grid}>
        {reels.map((c) => (
          <Pick
            key={c.key}
            c={c}
            picked={newSel.includes(c.key)}
            disabled={!newSel.includes(c.key) && newSel.length >= MAX}
            onPick={() => toggle(newSel, setNewSel, c.key)}
          />
        ))}
      </div>

      <div className='d-flex justify-content-between align-items-end flex-wrap gap-2 mb-3'>
        <div>
          <h4 className='fw-bold mb-1'>2. סרטונים ממודעות קודמות — בחרו {MAX}</h4>
          <div className='text-muted fs-8'>
            נבחרו {oldSel.length} מתוך {MAX}. 🏆 = הכי זולים לליד בעבר (10+ לידים).
          </div>
        </div>
        <div className='d-flex gap-2'>
          <div className='btn-group btn-group-sm'>
            <button
              className={`btn ${oldSort === 'cpl' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setOldSort('cpl')}
            >
              הכי זול לליד
            </button>
            <button
              className={`btn ${oldSort === 'spend' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setOldSort('spend')}
            >
              הכי הרבה הוצאה
            </button>
          </div>
          <button
            className='btn btn-sm btn-light-success'
            onClick={() => setOldSel(proven.slice(0, MAX).map((c) => c.key))}
          >
            בחירה אוטומטית: {MAX} הטובים
          </button>
        </div>
      </div>
      <div className='d-grid gap-3 mb-6' style={grid}>
        {olds.map((c) => (
          <Pick
            key={c.key}
            c={c}
            picked={oldSel.includes(c.key)}
            disabled={!oldSel.includes(c.key) && oldSel.length >= MAX}
            onPick={() => toggle(oldSel, setOldSel, c.key)}
            badge={topOld.includes(c.key) ? `🏆 #${topOld.indexOf(c.key) + 1}` : undefined}
          />
        ))}
      </div>

      <h4 className='fw-bold mb-3'>3. איך להריץ</h4>
      <div className='d-flex gap-3 flex-wrap mb-4'>
        {(
          [
            [
              'groups',
              'חדשים מול ישנים (מומלץ)',
              'לכל דרך: מודעה עם 4 החדשים ומודעה עם 4 הישנים, אותו תקציב. רואים אם החדשים עדיפים, ובתוך כל קבוצה פייסבוק מוצא את הסרטון המנצח.',
            ],
            [
              'cells',
              'כל סרטון בנפרד',
              'כל סרטון × דרך = מודעה עם תקציב משלה. השוואה הכי מדויקת, אבל יקר יותר (עד 12 מודעות).',
            ],
          ] as const
        ).map(([k, label, note]) => (
          <label
            key={k}
            className='form-check border rounded p-3 ps-10 m-0'
            style={{flex: '1 1 300px', background: layout === k ? '#eef4ff' : undefined}}
          >
            <input
              className='form-check-input'
              type='radio'
              name='launch-layout'
              checked={layout === k}
              onChange={() => setLayout(k)}
            />
            <span className='form-check-label'>
              <b className='fs-7'>{label}</b>
              <div className='text-muted fs-8'>{note}</div>
            </span>
          </label>
        ))}
      </div>
      <div className='row g-3 mb-4'>
        <div className='col-md-5'>
          <label className='fs-8 text-muted d-block mb-1'>לאן הלחיצה מובילה</label>
          {(
            [
              ['website', '🌐 דף הרשמה'],
              ['lead_form', '📝 טופס פייסבוק'],
            ] as const
          ).map(([k, l]) => (
            <label key={k} className='form-check form-check-inline'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={flows.includes(k)}
                onChange={(e) =>
                  setFlows(e.target.checked ? [...flows, k] : flows.filter((x) => x !== k))
                }
              />
              <span className='form-check-label fs-7'>{l}</span>
            </label>
          ))}
          <div className='text-muted fs-8'>וואטסאפ — אחרי קישור הדף למספר.</div>
        </div>
        <div className='col-md-4'>
          <label className='fs-8 text-muted'>קהל</label>
          <select
            className='form-select form-select-sm'
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
          >
            <option value='broad'>רחב — Advantage+</option>
            {lookalikes.includes('lal_1') && (
              <option value='lal_1'>Lookalike 1% (דומים ללקוחות משלמים)</option>
            )}
            {lookalikes.includes('lal_3') && <option value='lal_3'>Lookalike 3%</option>}
          </select>
        </div>
        <div className='col-md-3'>
          <label className='fs-8 text-muted'>תקציב יומי לכל מודעה ₪</label>
          <input
            type='number'
            min={20}
            className='form-control form-control-sm'
            value={budget}
            onChange={(e) => setBudget(Number(e.target.value))}
          />
        </div>
      </div>

      {msg && (
        <div className={`alert ${msg.ok ? 'alert-success' : 'alert-danger'} fs-7`}>
          {msg.text}{' '}
          {msg.ok && (
            <button className='btn btn-sm btn-light-primary ms-2' onClick={goTests}>
              לבדיקות A/B
            </button>
          )}
        </div>
      )}

      <div
        className='d-flex align-items-center gap-3 flex-wrap p-4 shadow'
        style={{
          position: 'fixed',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#1e293b',
          color: '#fff',
          borderRadius: 14,
          zIndex: 50,
          maxWidth: 'calc(100vw - 32px)',
        }}
      >
        <span className='fs-7'>
          חדשים{' '}
          <b>
            {newSel.length}/{MAX}
          </b>{' '}
          · ישנים{' '}
          <b>
            {oldSel.length}/{MAX}
          </b>{' '}
          · <b>{ads}</b> מודעות · <b>₪{perDay.toLocaleString('he-IL')}</b> ליום
        </span>
        {problem ? (
          <span className='text-warning fs-8'>{problem}</span>
        ) : (
          <>
            <button className='btn btn-sm btn-light' disabled={busy} onClick={() => submit(false)}>
              צור מושהה
            </button>
            <button className='btn btn-sm btn-success' disabled={busy} onClick={() => submit(true)}>
              {busy ? 'יוצר…' : '🚀 צור והפעל'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default LaunchTest
